import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Save } from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { newsApi } from '../../lib/api'
import RichTextEditor from '../../components/ui/RichTextEditor'

interface Category {
    id: number
    slug: string
    name_en: string
    name_vi: string
    icon: string
}

export default function NewsEditor() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { language } = useLanguageStore()

    const isEdit = !!id

    const [formData, setFormData] = useState({
        title_en: '',
        title_vi: '',
        slug: '',
        excerpt_en: '',
        excerpt_vi: '',
        content_en: '',
        content_vi: '',
        category: '',
        location: '',
        featured: false,
        status: 'draft',
        featuredImage: '',
    })

    const [activeTab, setActiveTab] = useState<'en' | 'vi'>('vi')
    const [saving, setSaving] = useState(false)
    const [loading, setLoading] = useState(false)
    const [categories, setCategories] = useState<Category[]>([])
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

    // Fetch categories from DB
    useEffect(() => {
        fetch('/api/categories')
            .then(r => r.json())
            .then(data => {
                if (data.categories) {
                    setCategories(data.categories)
                    // Set default category if empty
                    if (!formData.category && data.categories.length > 0) {
                        setFormData(prev => ({ ...prev, category: data.categories[0].slug }))
                    }
                }
            })
            .catch(err => console.error('Failed to load categories:', err))
    }, [])

    // Fetch article data when editing
    useEffect(() => {
        if (isEdit && id) {
            setLoading(true)
            newsApi.getById(parseInt(id))
                .then((response) => {
                    const apiResponse = response.data as any
                    const article = apiResponse?.data || apiResponse

                    if (article && article.id) {
                        setFormData({
                            title_en: article.title_en || '',
                            title_vi: article.title_vi || '',
                            slug: article.slug || '',
                            excerpt_en: article.excerpt_en || '',
                            excerpt_vi: article.excerpt_vi || '',
                            content_en: article.content_en || '',
                            content_vi: article.content_vi || '',
                            category: article.category || '',
                            location: article.location || '',
                            featured: article.featured === 1 || article.featured === true,
                            status: article.status || 'draft',
                            featuredImage: article.featured_image || article.featuredImage || '',
                        })
                    }
                })
                .catch((err: Error) => console.error('Failed to fetch article:', err))
                .finally(() => setLoading(false))
        }
    }, [id, isEdit])

    const handleSave = async () => {
        setSaving(true)
        setMessage(null)

        try {
            const dataToSave = {
                ...formData,
                featured_image: formData.featuredImage,
            }

            if (isEdit && id) {
                const response = await newsApi.update(parseInt(id), dataToSave)
                if (response.error) {
                    throw new Error(response.error)
                }
                setMessage({ type: 'success', text: language === 'en' ? 'Article saved!' : 'Bài viết đã được lưu!' })
            } else {
                const response = await newsApi.create(dataToSave)
                if (response.error) {
                    throw new Error(response.error)
                }
                setMessage({ type: 'success', text: language === 'en' ? 'Article created!' : 'Bài viết đã được tạo!' })
                // Redirect to edit page after create
                if (response.data?.id) {
                    navigate(`/admin/blog/${response.data.id}/edit`)
                }
            }
        } catch (error) {
            console.error('Failed to save:', error)
            setMessage({
                type: 'error',
                text: language === 'en' ? 'Failed to save article' : 'Lưu bài viết thất bại'
            })
        } finally {
            setSaving(false)
        }
    }

    const getCategoryName = (cat: Category) => language === 'en' ? cat.name_en : cat.name_vi

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/admin/blog')}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {isEdit
                                ? (language === 'en' ? 'Edit Blog Post' : 'Sửa Bài Viết')
                                : (language === 'en' ? 'New Blog Post' : 'Bài Viết Mới')
                            }
                        </h1>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {language === 'en' ? 'Save' : 'Lưu'}
                    </button>
                </div>
            </div>

            {/* Message */}
            {message && (
                <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {message.text}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        {/* Language Tabs */}
                        <div className="flex gap-2 mb-6">
                            <button
                                onClick={() => setActiveTab('vi')}
                                className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === 'vi'
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                🇻🇳 Tiếng Việt
                            </button>
                            <button
                                onClick={() => setActiveTab('en')}
                                className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === 'en'
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                🇬🇧 English
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    {activeTab === 'en' ? 'Title (English)' : 'Tiêu đề (Tiếng Việt)'} *
                                </label>
                                <input
                                    type="text"
                                    value={activeTab === 'en' ? formData.title_en : formData.title_vi}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        [activeTab === 'en' ? 'title_en' : 'title_vi']: e.target.value
                                    })}
                                    placeholder={activeTab === 'en' ? 'Enter article title' : 'Nhập tiêu đề bài viết'}
                                    className="w-full px-4 py-3 text-xl border rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                            </div>

                            {/* Excerpt */}
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    {activeTab === 'en' ? 'Excerpt (English)' : 'Tóm tắt (Tiếng Việt)'}
                                </label>
                                <textarea
                                    value={activeTab === 'en' ? formData.excerpt_en : formData.excerpt_vi}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        [activeTab === 'en' ? 'excerpt_en' : 'excerpt_vi']: e.target.value
                                    })}
                                    placeholder={activeTab === 'en' ? 'Short description for previews...' : 'Mô tả ngắn cho preview...'}
                                    rows={2}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                            </div>

                            {/* Content Editor - Use key to force remount when tab changes */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    {activeTab === 'en' ? 'Content (English)' : 'Nội dung (Tiếng Việt)'} *
                                </label>
                                <RichTextEditor
                                    key={activeTab}
                                    content={activeTab === 'en' ? formData.content_en : formData.content_vi}
                                    onChange={(content) => setFormData({
                                        ...formData,
                                        [activeTab === 'en' ? 'content_en' : 'content_vi']: content
                                    })}
                                    placeholder={activeTab === 'en' ? 'Write your article content here...' : 'Viết nội dung bài viết tại đây...'}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Publish Settings */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">{language === 'en' ? 'Publish' : 'Xuất bản'}</h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    {language === 'en' ? 'Status' : 'Trạng thái'}
                                </label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                >
                                    <option value="draft">{language === 'en' ? 'Draft' : 'Nháp'}</option>
                                    <option value="published">{language === 'en' ? 'Published' : 'Đã xuất bản'}</option>
                                </select>
                            </div>

                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.featured}
                                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                                    className="rounded"
                                />
                                <span className="text-sm">{language === 'en' ? 'Featured article' : 'Bài viết nổi bật'}</span>
                            </label>
                        </div>
                    </div>

                    {/* Category */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">{language === 'en' ? 'Category' : 'Danh mục'}</h3>
                        <select
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                        >
                            <option value="">{language === 'en' ? 'Select category' : 'Chọn danh mục'}</option>
                            {categories.map((cat) => (
                                <option key={cat.id} value={cat.slug}>
                                    {getCategoryName(cat)}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Location */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">{language === 'en' ? 'Location' : 'Vị trí'}</h3>
                        <select
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                        >
                            <option value="">{language === 'en' ? 'Select location' : 'Chọn vị trí'}</option>
                            <option value="duong-dong">Dương Đông</option>
                            <option value="an-thoi">{language === 'en' ? 'An Thoi' : 'An Thới'}</option>
                            <option value="bai-sao">{language === 'en' ? 'Bai Sao Beach' : 'Bãi Sao'}</option>
                            <option value="bai-truong">{language === 'en' ? 'Bai Truong Beach' : 'Bãi Trường'}</option>
                            <option value="ganh-dau">{language === 'en' ? 'Ganh Dau' : 'Gành Dầu'}</option>
                            <option value="ham-ninh">{language === 'en' ? 'Ham Ninh' : 'Hàm Ninh'}</option>
                            <option value="khu-bac">{language === 'en' ? 'Northern Area' : 'Khu Bắc'}</option>
                            <option value="khu-nam">{language === 'en' ? 'Southern Area' : 'Khu Nam'}</option>
                            <option value="phu-quoc">{language === 'en' ? 'Phu Quoc (General)' : 'Phú Quốc (Chung)'}</option>
                        </select>
                    </div>

                    {/* Slug */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">Slug</h3>
                        <input
                            type="text"
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                            placeholder="article-url-slug"
                            className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {language === 'en' ? 'Leave empty to auto-generate from title' : 'Để trống để tự động tạo từ tiêu đề'}
                        </p>
                    </div>

                    {/* Featured Image */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">{language === 'en' ? 'Featured Image' : 'Ảnh đại diện'}</h3>
                        <input
                            type="url"
                            value={formData.featuredImage}
                            onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                            placeholder="https://..."
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                        {formData.featuredImage && (
                            <img
                                src={formData.featuredImage}
                                alt="Preview"
                                className="mt-3 rounded-lg w-full h-32 object-cover"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
